import atBreakpointChange from "../lib/at-breakpoint-change.js"
import EVENTS from "../lib/events.js"
import getMediaQuery from "../lib/media-queries.js"

const { ANNOUNCEMENT_HEIGHT_UPDATED, HEADER_HEIGHT_UPDATED } = EVENTS

class CollectionProductGrid extends HTMLElement {
  constructor() {
    super()

    this.selectors = {
      activeFilters: "[js-active-filters]",
      announcementBar: "announcement-bar",
      disclosureWrapper: ".disclosure-wrapper",
      filterDrawerTrigger: "[js-filter-drawer-trigger]",
      stickyFilterDrawerTrigger: "[js-filter-drawer-trigger--sticky]",
      filterDrawerHeading: "[js-filter-drawer-heading]",
      filterDrawerViewResults: "[js-filter-view-results]",
      filterDrawerViewResultsIdleLabel: "[js-filter-view-results-label]",
      filterGroup: "[js-filter-group]",
      filterGroupParent: "[js-filter-group-parent]",
      filterSortFormComponent: "[js-filter-sort-form-component]",
      footer: "footer",
      header: "header-wrapper",
      pagination: "[js-pagination]",
      productGrid: "[js-product-grid]",
      promotionTile: "[js-promotion-tile]",
      promotionTilevideo: "[js-promotion-tile] [js-background-video]",
      sortDrawerTrigger: "[js-sort-drawer-trigger]",
      sortDropdowns:
        "[js-sort-top-bar], [js-sort-sticky-button], [js-sort-mobile-drawer]",
      sortTriggerValue: "[js-sort-trigger-value]",
      stickyFilterSort: "[js-sticky-filter-sort-button]",
      topBar: "[js-product-grid-top-bar]",
    }

    this.classes = {
      active: "active",
      hidden: "hidden",
      loading: "loading",
      visible: "is-visible",
    }

    this.breakpoints = {
      smallDesktop: getMediaQuery("small-desktop"),
    }

    this.events = {
      openFilterDrawer: new CustomEvent("fluco:modal:filterDrawer:open"),
      closeFilterDrawer: new CustomEvent("fluco:modal:filterDrawer:close"),
      openSortDrawer: new CustomEvent("fluco:modal:mobileSortDrawer:open"),
      closeSortDrawer: new CustomEvent("fluco:modal:mobileSortDrawer:close"),
    }
  }

  connectedCallback() {
    this.controller = new AbortController()
    this.sectionId = this.dataset.sectionId
    this.sectionPaddingTop = parseInt(
      getComputedStyle(this)
        .getPropertyValue("--section-padding-top")
        .replace("px", ""),
      10,
    )
    this.eventOptions = { signal: this.controller.signal }

    this.header = document.querySelector(this.selectors.header)
    this.announcementBar = document.querySelector(
      this.selectors.announcementBar,
    )
    this.headerHeight = 0
    this.announcementHeight = 0
    this.getHeaderAnnouncementHeight()
    document.addEventListener(
      HEADER_HEIGHT_UPDATED,
      this.handleHeaderHeightChange.bind(this),
      this.eventOptions,
    )
    document.addEventListener(
      ANNOUNCEMENT_HEIGHT_UPDATED,
      this.handleAnnouncementHeightChange.bind(this),
      this.eventOptions,
    )

    this.filterDrawerTriggers = this.querySelectorAll(
      `${this.selectors.filterDrawerTrigger}, ${this.selectors.stickyFilterDrawerTrigger}`,
    )
    this.filterDrawerHeading = this.querySelector(
      this.selectors.filterDrawerHeading,
    )
    this.filterDrawerViewResults = this.querySelector(
      this.selectors.filterDrawerViewResults,
    )
    this.filterDrawerViewResultsIdleLabels = this.querySelectorAll(
      this.selectors.filterDrawerViewResultsIdleLabel,
    )
    this.activeFilters = this.querySelector(this.selectors.activeFilters)
    this.filterGroups = this.querySelectorAll(this.selectors.filterGroup)
    this.topBar = this.querySelector(this.selectors.topBar)
    this.sortTriggerValue = this.querySelector(this.selectors.sortTriggerValue)
    this.sortDropdowns = this.querySelectorAll(this.selectors.sortDropdowns)
    this.productGrid = this.querySelector(this.selectors.productGrid)
    this.promotionTiles = this.querySelectorAll(this.selectors.promotionTile)
    this.filterSortFormComponent = this.querySelector(
      this.selectors.filterSortFormComponent,
    )
    this.pagination = this.querySelector(this.selectors.pagination)

    this.initFilterDrawerTriggers()

    this.filterDrawerViewResults?.addEventListener(
      "click",
      this.handleViewResultsClick.bind(this),
      this.eventOptions,
    )

    this.addEventListener(
      "fluco:filter-sort:changed",
      this.handleFilterSortFormChange.bind(this),
      this.eventOptions,
    )

    this.initSortDropdowns()

    if (
      this.dataset.filterEnabled === "true" ||
      this.dataset.sortEnabled === "true"
    ) {
      this.initStickyFilterSortButton()
    }

    this.sortDrawerTrigger.addEventListener(
      "click",
      this.openSortDrawer.bind(this),
      this.eventOptions,
    )

    // Show the sticky filter/sort button when the mobile sort drawer is closed
    window.addEventListener(
      "fluco:modal:mobileSortDrawer:closed",
      this.showStickyFilterSort.bind(this),
      this.eventOptions,
    )
  }

  initFilterDrawerTriggers() {
    this.filterDrawerTriggers.forEach((trigger) => {
      trigger.addEventListener(
        "click",
        this.handleFilterDrawerOpen.bind(this),
        this.eventOptions,
      )
    })

    // Check whether we should show the sticky filter/sort button when the filter drawer is closed
    window.addEventListener(
      "fluco:modal:filterDrawer:closed",
      this.handleFilterDrawerClose.bind(this),
      this.eventOptions,
    )
  }

  initStickyFilterSortButton() {
    this.siteFooter = document.querySelector(this.selectors.footer)
    this.stickyFilterSort = this.querySelector(this.selectors.stickyFilterSort)
    this.sortDrawerTrigger = this.querySelector(
      this.selectors.sortDrawerTrigger,
    ).parentNode

    this.mobileFooterObserver = new IntersectionObserver(
      ([{ isIntersecting: visible }]) => {
        if (visible) {
          this.mobileTopBarObserver.disconnect()
          this.hideStickyFilterSort()
        } else {
          this.mobileTopBarObserver.observe(this.topBar)
          this.showStickyFilterSort()
        }
      },
      { threshold: 0.05 },
    )

    this.mobileTopBarObserver = new IntersectionObserver(
      ([{ isIntersecting: visible, boundingClientRect }]) => {
        if (visible) {
          this.showStickyFilterSort()
        } else {
          if (boundingClientRect.top <= 0) {
            this.showStickyFilterSort()
          } else {
            this.hideStickyFilterSort()
          }
        }
      },
    )

    if (this.dataset.stickyFilterSortEnabled === "true") {
      this.footerObserver = new IntersectionObserver(
        ([{ isIntersecting: visible }]) => {
          if (visible) {
            this.topBarObserver.disconnect()
            this.hideStickyFilterSort()
          } else {
            this.topBarObserver.observe(this.topBar)
          }
        },
        { threshold: 0.1 },
      )

      this.topBarObserver = new IntersectionObserver(
        ([{ isIntersecting: visible, boundingClientRect }]) => {
          if (visible) {
            this.topBar.classList.add(this.classes.visible)
            this.hideStickyFilterSort()
          } else {
            this.topBar.classList.remove(this.classes.visible)

            if (
              boundingClientRect.top <=
              this.headerHeight + this.announcementHeight
            ) {
              this.showStickyFilterSort()
            } else {
              this.hideStickyFilterSort()
            }
          }
        },
        {
          rootMargin: `-${
            this.headerHeight + this.announcementHeight
          }px 0px 0px 0px`,
        },
      )
    }

    // Watch for small-desktop breakpoint to handle sticky/filter button visibility
    atBreakpointChange(1024, this.handleWindowResize.bind(this))
    this.handleWindowResize()
  }

  handleFilterDrawerOpen(event) {
    this.hideStickyFilterSort()

    // If the open was triggered by the sticky filter/sort button, we need to delay
    //  to allow the button to animate out.
    // Otherwise, just open
    if (event.target.matches(this.selectors.stickyFilterDrawerTrigger)) {
      setTimeout(() => {
        window.dispatchEvent(this.events.openFilterDrawer)
      }, 240)
    } else {
      window.dispatchEvent(this.events.openFilterDrawer)
    }
  }

  handleFilterDrawerClose() {
    if (
      !this.topBar.classList.contains(this.classes.visible) &&
      this.dataset.stickyFilterSortEnabled === "true"
    ) {
      this.showStickyFilterSort()
    }
  }

  handleViewResultsClick() {
    window.dispatchEvent(this.events.closeFilterDrawer)
  }

  handleFilterSortFormChange(event) {
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${event.detail.searchParams}`

    const topBarScrollTop =
      this.topBar.getBoundingClientRect().top +
      window.scrollY -
      this.sectionPaddingTop -
      this.headerHeight -
      this.announcementHeight

    // Scroll to the top bar
    window.scrollTo({ top: topBarScrollTop, behaviour: "smooth" })

    this.updateURLHash(event.detail.searchParams)
    this.classList.add(this.classes.loading)

    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, "text/html")

        const newProductGrid = doc.querySelector(this.selectors.productGrid)
        const newPagination = doc.querySelector(this.selectors.pagination)

        if (this.dataset.filterEnabled === "true") {
          const newFilterDrawerHeading = doc.querySelector(
            this.selectors.filterDrawerHeading,
          )
          const newFilterDrawerViewResultsIdleLabel = doc.querySelector(
            this.selectors.filterDrawerViewResultsIdleLabel,
          )
          const newActiveFilters = doc.querySelector(
            this.selectors.activeFilters,
          )
          const newFilterDrawerTrigger = doc.querySelector(
            this.selectors.filterDrawerTrigger,
          )

          this.filterDrawerHeading.innerHTML = newFilterDrawerHeading.innerHTML

          this.filterDrawerViewResultsIdleLabels.forEach((label) => {
            label.innerHTML = newFilterDrawerViewResultsIdleLabel.innerHTML
          })

          this.activeFilters.innerHTML = newActiveFilters.innerHTML

          this.filterGroups.forEach((group) => {
            const newGroup = doc.getElementById(group.id)
            const groupParent = group.closest(this.selectors.filterGroupParent)

            if (newGroup) {
              groupParent.classList.remove(this.classes.hidden)
              group.innerHTML = newGroup.innerHTML
            } else {
              groupParent.classList.add(this.classes.hidden)
            }
          })

          this.filterDrawerTriggers.forEach((trigger) => {
            trigger.innerHTML = newFilterDrawerTrigger.innerHTML
          })
        }

        this.productGrid.innerHTML = newProductGrid.innerHTML
        this.productGrid.classList = newProductGrid.classList
        this.pagination.innerHTML = newPagination.innerHTML
        this.updateSortDropdowns(event.detail.sortBy)
        this.classList.remove(this.classes.loading)

        const filterRepaintEvent = new CustomEvent("fluco:filters:repaint", {
          detail: { newDocument: doc },
        })

        this.filterSortFormComponent.dispatchEvent(filterRepaintEvent)
      })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    )
  }

  initSortDropdowns() {
    this.sortDropdowns.forEach((dropdown) => {
      const inputs = dropdown.querySelectorAll("input")

      // Add a change listener to each input so that we can emit an event with the change
      inputs.forEach((input) => {
        input.addEventListener(
          "click",
          this.handleSortOptionClick.bind(this),
          this.eventOptions,
        )

        input.addEventListener(
          "keydown",
          this.handleSortOptionKeydown.bind(this),
          this.eventOptions,
        )
      })
    })
  }

  updateSortDropdowns(sortBy) {
    this.dataset.currentSort = sortBy

    this.sortDropdowns.forEach((dropdown) => {
      const newSelectedInput = dropdown.querySelector(
        `input[value="${sortBy}"]`,
      )
      newSelectedInput.checked = true
    })
  }

  handleSortOptionKeydown(event) {
    event.preventDefault()

    const inputs = event.currentTarget
      .closest(this.selectors.sortDropdowns)
      .querySelectorAll("input")
    let currentInputIndex

    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i] === event.target) {
        currentInputIndex = i
        break
      }
    }

    switch (event.key) {
      case "ArrowUp": {
        let previousInputIndex = currentInputIndex - 1

        // If the current input is the first, then the "previous" should be the last
        if (previousInputIndex < 0) {
          previousInputIndex = inputs.length - 1
        }

        inputs[previousInputIndex].checked = true
        inputs[previousInputIndex].focus()
        break
      }

      case "ArrowDown": {
        let nextInputIndex = currentInputIndex + 1

        // If the current input is the first, then the "previous" should be the last
        if (nextInputIndex === inputs.length) {
          nextInputIndex = 0
        }

        inputs[nextInputIndex].checked = true
        inputs[nextInputIndex].focus()
        break
      }

      case " ": {
        if (event.target.value === this.dataset.currentSort) {
          this.closeSortDropdown(
            event.target.closest(this.selectors.sortDropdowns),
          )
        } else {
          this.applySortChange(
            event.target.value,
            event.target.dataset.label,
            event.target.closest(this.selectors.sortDropdowns),
          )
        }
      }
    }
  }

  handleSortOptionClick(event) {
    if (event.target.value !== this.dataset.currentSort) {
      this.applySortChange(
        event.target.value,
        event.target.dataset.label,
        event.target.closest(this.selectors.sortDropdowns),
      )
    }
  }

  applySortChange(sortBy, label, parentDropdown) {
    const sortChangedEvent = new CustomEvent("fluco:sort:changed", {
      detail: { sortBy },
    })

    this.filterSortFormComponent.dispatchEvent(sortChangedEvent)
    this.sortTriggerValue.innerText = label
    this.closeSortDropdown(parentDropdown)
  }

  closeSortDropdown(dropdown) {
    const disclosureWrapper = dropdown.closest(this.selectors.disclosureWrapper)

    if (disclosureWrapper) {
      disclosureWrapper.removeAttribute("open")
    } else {
      this.closeSortDrawer()
    }
  }

  handleWindowResize() {
    // If we're above small-desktop, connect an observer to show/hide the sticky
    // filter/sort button when the top bar or footer are in view.
    // Otherwise, connect a mobile-specific observer for when the footer or
    // banner are in view.
    if (window.matchMedia(this.breakpoints.smallDesktop).matches) {
      this.hideStickyFilterSort()
      this.mobileFooterObserver.disconnect()
      this.mobileTopBarObserver.disconnect()

      if (this.dataset.stickyFilterSortEnabled === "true") {
        this.footerObserver.observe(this.siteFooter)
      }
    } else {
      this.hideStickyFilterSort()

      if (this.dataset.stickyFilterSortEnabled === "true") {
        this.footerObserver.disconnect()
        this.topBarObserver.disconnect()
      }

      this.mobileFooterObserver.observe(this.siteFooter)
    }
  }

  openSortDrawer() {
    this.hideStickyFilterSort()

    // Delay to allow sticky filter/sort button to animate out.
    setTimeout(() => {
      window.dispatchEvent(this.events.openSortDrawer)
    }, 240)
  }

  closeSortDrawer() {
    window.dispatchEvent(this.events.closeSortDrawer)
  }

  hideStickyFilterSort() {
    this.stickyFilterSort?.classList.remove(this.classes.active)
  }

  showStickyFilterSort() {
    this.stickyFilterSort?.classList.add(this.classes.active)
  }

  getHeaderAnnouncementHeight() {
    if (this.header.dataset.enableStickyHeader === "true") {
      this.headerHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--header-height")
          .replace("px", ""),
        10,
      )
    }

    if (
      this.announcementBar &&
      this.announcementBar.hasAttribute("data-sticky")
    ) {
      this.announcementHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue("--announcement-height")
          .replace("px", ""),
        10,
      )
    }
  }

  handleHeaderHeightChange(event) {
    if (this.header.dataset.enableStickyHeader === "true") {
      this.headerHeight = event.detail.height
      this.mobileFooterObserver.disconnect()
      this.mobileTopBarObserver.disconnect()
      this.footerObserver?.disconnect()
      this.topBarObserver?.disconnect()
      this.initStickyFilterSortButton()
    }
  }

  handleAnnouncementHeightChange(event) {
    if (
      this.announcementBar &&
      this.announcementBar.hasAttribute("data-sticky")
    ) {
      this.announcementHeight = event.detail.height
      this.mobileFooterObserver.disconnect()
      this.mobileTopBarObserver.disconnect()
      this.footerObserver?.disconnect()
      this.topBarObserver?.disconnect()
      this.initStickyFilterSortButton()
    }
  }

  disconnectedCallback() {
    this.controller.abort()
    this.documentObserver.disconnect()
    this.mobileFooterObserver.disconnect()
    this.mobileTopBarObserver.disconnect()
    this.footerObserver?.disconnect()
    this.topBarObserver?.disconnect()
    atBreakpointChange.unload()
  }
}

if (!customElements.get("collection-product-grid")) {
  customElements.define("collection-product-grid", CollectionProductGrid)
}
